import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Bell, 
  Info, 
  TriangleAlert, 
  CheckCircle2, 
  X, 
  TrendingUp, 
  TrendingDown, 
  ShieldAlert, 
  Flame, 
  Zap, 
  CreditCard, 
  PieChart,
  Moon,
  RotateCcw
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { fetchUserNotifications, markNotificationRead, markAllNotificationsRead, snoozePortfolioMddAlert, resetPortfolioHwm } from '../services/api';

export function getNotificationStyle(type: string) {
  switch (type) {
    case 'PORTFOLIO_STOP_LOSS':
    case 'PORTFOLIO_MDD':
    case 'SELL':
      return {
        bg: 'bg-red-500/15 border-red-500/30',
        iconColor: 'text-red-400',
        Icon: ShieldAlert
      };
    case 'OIL_SPIKE':
      return {
        bg: 'bg-orange-500/15 border-orange-500/30',
        iconColor: 'text-orange-400',
        Icon: Flame
      };
    case 'VIX_RECOVERY_BUY':
    case 'BUY':
      return {
        bg: 'bg-emerald-500/15 border-emerald-500/30',
        iconColor: 'text-emerald-400',
        Icon: TrendingUp
      };
    case 'PORTFOLIO_CONCENTRATION':
    case 'REBALANCE_DUE':
      return {
        bg: 'bg-indigo-500/15 border-indigo-500/30',
        iconColor: 'text-indigo-400',
        Icon: PieChart
      };
    case 'HITL_PENDING_APPROVAL':
    case 'EXECUTION_COMPLETED':
      return {
        bg: 'bg-purple-500/15 border-purple-500/30',
        iconColor: 'text-purple-400',
        Icon: Zap
      };
    case 'BILLING_UPCOMING':
      return {
        bg: 'bg-cyan-500/15 border-cyan-500/30',
        iconColor: 'text-cyan-400',
        Icon: CreditCard
      };
    case 'VIX_ALERT':
    case 'BOND_YIELD_SPIKE':
    case 'EXCHANGE_RATE_BREACH':
      return {
        bg: 'bg-amber-500/15 border-amber-500/30',
        iconColor: 'text-amber-400',
        Icon: TriangleAlert
      };
    default:
      return {
        bg: 'bg-blue-500/15 border-blue-500/20',
        iconColor: 'text-blue-400',
        Icon: Info
      };
  }
}

// ─── Toast Notification Popup ─────────────────────────────────────────────────
interface ToastProps {
  notification: any;
  onDismiss: () => void;
}

function NotificationToast({ notification, onDismiss }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 6000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const style = getNotificationStyle(notification.type);
  const Icon = style.Icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: 80, scale: 0.92 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.92 }}
      transition={{ type: 'spring', stiffness: 300, damping: 28 }}
      className={`flex items-start gap-3 p-4 rounded-2xl border backdrop-blur-xl shadow-2xl w-80 ${style.bg}`}
    >
      <div className={`mt-0.5 shrink-0 ${style.iconColor}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-white/90 truncate">{notification.title}</div>
        <div className="text-xs text-white/60 leading-relaxed mt-0.5 line-clamp-2">{notification.message}</div>
      </div>
      <button
        onClick={onDismiss}
        className="shrink-0 p-1 text-white/30 hover:text-white/70 transition-colors rounded-lg hover:bg-white/10"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  );
}

// ─── Notification Center ──────────────────────────────────────────────────────
export function NotificationCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toasts, setToasts] = useState<any[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Track IDs we've already shown toasts for (persisted across re-renders)
  const seenIdsRef = useRef<Set<string>>(new Set());
  const isFirstLoadRef = useRef(true);

  const loadNotifications = useCallback(async (isRefreshTrigger = false) => {
    try {
      const data = await fetchUserNotifications();
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

      const filtered = data.filter((n: any) => {
        const nDate = new Date(n.timestamp).getTime();
        const isToday = nDate >= today;
        if (isToday) return true;
        return !n.read;
      });

      setNotifications(filtered);
      const trueUnread = data.filter((n: any) => !n.read);
      setUnreadCount(trueUnread.length);

      // ── In-app toast for newly discovered unread notifications ──
      // Skip showing toasts on the very first silent load (page mount).
      // Only show toasts when:
      //   1. User manually clicked the refresh button (isRefreshTrigger = true)
      //   2. OR on subsequent periodic polls (not first load)
      if (!isFirstLoadRef.current || isRefreshTrigger) {
        const newOnes = trueUnread.filter((n: any) => !seenIdsRef.current.has(n.id));
        if (newOnes.length > 0) {
          // Show up to 3 toasts to avoid flooding
          newOnes.slice(0, 3).forEach((n: any) => {
            seenIdsRef.current.add(n.id);
            setToasts(prev => [...prev, { ...n, toastId: `toast-${n.id}-${Date.now()}` }]);
          });
        }
      }

      // After first load, mark all currently-seen unread IDs so periodic polls
      // only trigger toasts for brand-new signals
      if (isFirstLoadRef.current) {
        trueUnread.forEach((n: any) => seenIdsRef.current.add(n.id));
        isFirstLoadRef.current = false;
      }
    } catch (err) {
      console.error('Failed to load notifications', err);
    }
  }, []);

  // Initial load (silent — no toasts for existing unread)
  useEffect(() => {
    loadNotifications(false);
    // Periodic poll every 5 min — show toasts for truly new signals
    const interval = setInterval(() => loadNotifications(false), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Listen for refresh events dispatched by the Layout refresh button
  useEffect(() => {
    const handleRefresh = () => loadNotifications(true);
    window.addEventListener('app:refresh', handleRefresh);
    window.addEventListener('app:refresh-notifications', handleRefresh);
    return () => {
      window.removeEventListener('app:refresh', handleRefresh);
      window.removeEventListener('app:refresh-notifications', handleRefresh);
    };
  }, [loadNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkRead = async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await markNotificationRead(id);
    } catch (err) {
      console.error('Failed to mark notification as read', err);
      loadNotifications(false);
    }
  };

  const handleMarkAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch (err) {
      console.error('Failed to mark all notifications as read', err);
      loadNotifications(false);
    }
  };

  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const handleSnoozeMdd = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setProcessingId(id);
    try {
      const res = await snoozePortfolioMddAlert();
      setActionFeedback(res.message || '오늘 하루 알림이 음소거되었습니다.');
      setNotifications(prev => prev.map(n => (n.type === 'PORTFOLIO_MDD' || n.title.includes('MDD')) ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
      setTimeout(() => setActionFeedback(null), 4000);
    } catch (err: any) {
      console.error('Failed to snooze MDD alert:', err);
      setActionFeedback(err.message || '스누즈 처리에 실패했습니다.');
      setTimeout(() => setActionFeedback(null), 4000);
    } finally {
      setProcessingId(null);
    }
  };

  const handleResetHwmAction = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setProcessingId(id);
    try {
      const res = await resetPortfolioHwm();
      setActionFeedback(res.message || '고점(HWM)이 현재 자산 기준으로 동기화되었습니다.');
      setNotifications(prev => prev.map(n => (n.type === 'PORTFOLIO_MDD' || n.title.includes('MDD')) ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
      setTimeout(() => setActionFeedback(null), 4000);
    } catch (err: any) {
      console.error('Failed to reset HWM:', err);
      setActionFeedback(err.message || 'HWM 동기화에 실패했습니다.');
      setTimeout(() => setActionFeedback(null), 4000);
    } finally {
      setProcessingId(null);
    }
  };

  const dismissToast = (toastId: string) => {
    setToasts(prev => prev.filter(t => t.toastId !== toastId));
  };

  const getIcon = (type: string) => {
    const style = getNotificationStyle(type);
    const Icon = style.Icon;
    return <Icon className={`w-4 h-4 ${style.iconColor}`} />;
  };

  return (
    <>
      {/* ── In-app Toast Stack (top-right) ── */}
      <div className="fixed top-24 right-6 z-[200] flex flex-col gap-3 pointer-events-none">
        <AnimatePresence>
          {toasts.map(toast => (
            <div key={toast.toastId} className="pointer-events-auto">
              <NotificationToast
                notification={toast}
                onDismiss={() => dismissToast(toast.toastId)}
              />
            </div>
          ))}
        </AnimatePresence>
      </div>

      {/* ── Bell Icon + Dropdown ── */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) loadNotifications(false);
          }}
          className="relative p-2 text-white/60 hover:text-white hover:bg-white/5 rounded-xl transition-all"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 border-2 border-black rounded-full" />
          )}
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 mt-3 w-80 bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden"
            >
              <div className="p-4 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
                <div className="flex flex-col">
                  <h3 className="font-bold text-sm">Notifications</h3>
                  <span className="text-[10px] text-white/30 uppercase font-black tracking-tighter">
                    {unreadCount} UNREAD
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="text-[10px] text-emerald-400 hover:text-emerald-300 font-bold uppercase transition-colors px-2 py-1 bg-emerald-500/10 rounded-lg border border-emerald-500/20"
                    >
                      Mark All Read
                    </button>
                  )}
                  <button onClick={() => setIsOpen(false)} className="p-1 text-white/20 hover:text-white rounded-lg hover:bg-white/5 transition-all">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {actionFeedback && (
                <div className="mx-3 my-2 p-2 rounded-lg bg-blue-500/20 border border-blue-500/30 text-[11px] text-blue-300 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <span>{actionFeedback}</span>
                </div>
              )}

              <div className="max-h-96 overflow-y-auto scrollbar-hide">
                {notifications.length > 0 ? (
                  notifications.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => !n.read && handleMarkRead(n.id)}
                      className={`p-4 border-b border-white/5 transition-all cursor-pointer ${n.read ? 'opacity-20 grayscale' : 'hover:bg-white/5'}`}
                    >
                      <div className="flex gap-3">
                        <div className="mt-1">{getIcon(n.type)}</div>
                        <div className="space-y-1 flex-1">
                          <div className={`text-sm font-bold ${n.read ? 'text-white/40' : 'text-white/90'}`}>{n.title}</div>
                          <div className={`text-xs leading-relaxed ${n.read ? 'text-white/20' : 'text-white/50'}`}>{n.message}</div>
                          
                          {(n.type === 'PORTFOLIO_MDD' || n.title?.includes('MDD')) && !n.read && (
                            <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                              <button
                                onClick={(e) => handleSnoozeMdd(e, n.id)}
                                disabled={processingId === n.id}
                                className="px-2 py-1 bg-white/5 hover:bg-white/10 text-[10px] text-white/70 hover:text-white rounded border border-white/10 flex items-center gap-1 transition-all"
                                title="24시간 동안 추가 알림을 받지 않습니다"
                              >
                                <Moon className="w-2.5 h-2.5 text-blue-400" />
                                오늘 하루 끄기
                              </button>
                              <button
                                onClick={(e) => handleResetHwmAction(e, n.id)}
                                disabled={processingId === n.id}
                                className="px-2 py-1 bg-blue-600/20 hover:bg-blue-600/40 text-[10px] text-blue-300 hover:text-white rounded border border-blue-500/30 flex items-center gap-1 transition-all"
                                title="현재 자산 가치를 새 고점 기준으로 재설정합니다"
                              >
                                <RotateCcw className="w-2.5 h-2.5 text-blue-400" />
                                HWM 재동기화
                              </button>
                            </div>
                          )}

                          <div className="flex items-center justify-between mt-2">
                            <div className="text-[9px] text-white/20 font-mono italic">
                              {new Date(n.timestamp).toLocaleString()}
                            </div>
                            {n.read ? (
                              <span className="text-[8px] text-white/20 uppercase font-bold">Confirmed</span>
                            ) : (
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-10 text-center space-y-2">
                    <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mx-auto">
                      <Bell className="w-6 h-6 text-white/10" />
                    </div>
                    <div className="text-sm text-white/30 italic">알림 내역이 없습니다.</div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
